/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useRef, useState } from "react";

import { Key } from "ts-key-enum";
import { Icon, ICON_SIZE } from "../Icon";
import { BEM } from "@adapptio/lib-components";
import { isFunction, isNil, isString } from "@adapptio/lib-shared";

import { useToggle } from "@adapptio/lib-components/src/Hooks/useToggle";
import { FadeInOut } from "../FadeInOut";
import { FC_ANCHOR, FC_DIRECTION, FC_SIZE, FloatingContainer } from "../FloatingContainer";
import { useEscKey } from "../../hooks/useEscKey";
import { List } from "../List";
import { Input } from "../Input";
import { InputAdornment } from "../InputAdornment";
import { InputButton } from "../InputButton";
import { useBackdropClick } from "@adapptio/lib-editor-ui";

function areEqualValues(a: unknown, b: unknown) {
	if (typeof b === "object" && b !== null) {
		return a === b;
	}

	return String(a) === String(b);
}

export interface ISelectProps {
	value: string;
	id?: string;
	placeholder?: string;
	readOnly?: boolean;
	allowCustomInput?: boolean;
	className?: string;
	popupClassName?: string;
	onChange?: (value: string) => void;
	renderValue?: (value: string) => { value?: string; icon?: string } | string;
	displayedValue?: string;
	displayedIcon?: string;
	grow?: boolean;
	startAdornment?: React.ReactNode;
	endAdornment?: React.ReactNode;
}

export const Select: FC<ISelectProps> = ({
	children,
	value,
	placeholder,
	readOnly,
	allowCustomInput: comboBox,
	onChange,
	className,
	popupClassName,
	renderValue,
	displayedValue,
	displayedIcon,
	id,
	startAdornment,
	endAdornment
}) => {
	const fieldControlRef = useRef<HTMLDivElement>();
	const inputRef = useRef<HTMLInputElement>();
	const containerRef = useRef();
	const [ focusOnListItem, setFocusOnListItem ] = useState(false);
	const [ dropdownOpen, , openDropdown, closeDropdown ] = useToggle();

	const renderedValue = isFunction(renderValue) ? renderValue(value) : null;

	const visibleValue =
		(!isNil(renderedValue)
			? isString(renderedValue)
				? renderedValue
				: renderedValue.value
			: displayedValue) || value;
	const visibleIcon =
		!isNil(renderedValue) && !isString(renderedValue) ? renderedValue.icon : displayedIcon;

	const baseClass = BEM("edt-select", null, {
		type: comboBox ? "combobox" : "select",
		open: dropdownOpen,
		readonly: readOnly
	});

	const tryOpenDropdown = useCallback(() => {
		if (dropdownOpen || readOnly) {
			return;
		}

		openDropdown();
	}, [ readOnly, openDropdown, dropdownOpen ]);

	const focusOnField = useCallback(() => {
		inputRef.current.focus();
		inputRef.current.selectionStart = inputRef.current.value.length;
		inputRef.current.selectionEnd = inputRef.current.value.length;
	}, [ inputRef ]);

	const handleInputChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
		(event) => {
			if (!comboBox) {
				return;
			}

			const value = event.target.value;
			onChange(value);
		},
		[ onChange ]
	);

	const handleInputKeyDown = useCallback<React.KeyboardEventHandler<HTMLInputElement>>(
		(event) => {
			switch (event.key) {
				case Key.Enter:
				case Key.Tab:
					// might be selecting an option
					if (dropdownOpen) {
						event.preventDefault();
					}
					break;
				case Key.ArrowDown:
					event.preventDefault();

					if (!dropdownOpen) {
						tryOpenDropdown();
						break;
					}

					if (!focusOnListItem) {
						setFocusOnListItem(true);
					}
					break;
				case Key.ArrowUp:
					if (dropdownOpen) {
						closeDropdown();
						break;
					}
					break;
				case " ":
					if (!dropdownOpen) {
						tryOpenDropdown();
						event.preventDefault();
						break;
					}

					if (!comboBox) {
						closeDropdown();
						setFocusOnListItem(false);
					}

					break;
			}
		},
		[ dropdownOpen, openDropdown ]
	);

	const handleOnWrapperClick = useCallback<React.MouseEventHandler>(
		(event) => {
			if (event.defaultPrevented || readOnly) {
				return;
			}
			tryOpenDropdown();

			if (focusOnListItem) {
				setFocusOnListItem(false);
			}
		},
		[ openDropdown, focusOnListItem, setFocusOnListItem, readOnly ]
	);

	const handleOnTriggerClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
		(event) => {
			if (event.defaultPrevented) {
				return;
			}

			event.stopPropagation();

			if (dropdownOpen) {
				if (focusOnListItem) {
					setFocusOnListItem(false);
				}
				return closeDropdown();
			}

			setFocusOnListItem(true);

			tryOpenDropdown();
		},
		[ openDropdown, closeDropdown, dropdownOpen, focusOnListItem, setFocusOnListItem ]
	);

	const handleItemClick = useCallback<(child: React.ReactNode) => React.MouseEventHandler<HTMLElement>>(
		(child) => (event) => {
			if (!React.isValidElement(child)) {
				return;
			}

			if (!event.currentTarget.hasAttribute("tabindex")) {
				return;
			}

			const newValue = child.props.value;

			if (isFunction(child.props.onClick)) {
				child.props.onClick(event);
			}

			if (newValue !== value) {
				if (isFunction(onChange)) {
					onChange(newValue);
				}
			}

			closeDropdown();
			focusOnField();
			setFocusOnListItem(false);
		},
		[ value, onChange, closeDropdown, setFocusOnListItem ]
	);

	const handleItemKeyUp = useCallback<(child: React.ReactNode) => React.KeyboardEventHandler<HTMLElement>>(
		(child) => (event) => {
			if (!React.isValidElement(child)) {
				return;
			}

			if (event.key === " ") {
				event.preventDefault();
			}

			if (isFunction(child.props.onKeyUp)) {
				child.props.onKeyUp(event);
			}
		},
		[]
	);

	const handleItemKeyDown = useCallback<React.KeyboardEventHandler<HTMLElement>>((event) => {
		if (event.key === Key.ArrowUp && !event.currentTarget.previousSibling) {
			closeDropdown();
			setFocusOnListItem(false);
			focusOnField();
		}
	}, []);

	const items = React.Children.toArray(children).map((child) => {
		if (!React.isValidElement(child)) {
			return null;
		}

		const selected = areEqualValues(child.props.value, value);

		return React.cloneElement(child, {
			"aria-selected": selected,
			selected,
			onClick: handleItemClick(child),
			onKeyUp: handleItemKeyUp(child),
			onKeyDown: handleItemKeyDown,
			role: "option",
			tabIndex: child.props.tabIndex || (selected ? 0 : -1),
			button: true,
			value: undefined, // most likely not valid HTML attribute,
			"data-value": child.props.value
		} as React.HTMLAttributes<unknown>);
	});

	if (className) {
		baseClass.add(className);
	}

	useEscKey(() => {
		closeDropdown();
		focusOnField();
	}, dropdownOpen);

	useBackdropClick(
		(event) => {
			if (event.defaultPrevented || !dropdownOpen) {
				return;
			}

			event.preventDefault();
			closeDropdown();
		},
		[ inputRef, containerRef ],
		dropdownOpen
	);

	return (
		<>
			<div
				className={baseClass.element("wrapper").toClassName()}
				role="button"
				aria-haspopup="listbox"
				aria-expanded={dropdownOpen}
				onClick={handleOnWrapperClick}
				ref={fieldControlRef}
			>
				<Input
					id={id}
					type="text"
					ref={inputRef}
					className={baseClass.toClassName()}
					readOnly={!comboBox || readOnly}
					placeholder={placeholder}
					value={visibleValue}
					onChange={handleInputChange}
					onKeyDown={handleInputKeyDown}
					startAdornment={
						startAdornment || visibleIcon ? (
							<>
								{startAdornment}
								{visibleIcon && (
									<InputAdornment square center>
										<Icon
											className={baseClass.element("icon").toClassName()}
											name={visibleIcon}
											size={ICON_SIZE.XSMALL}
										/>
									</InputAdornment>
								)}
							</>
						) : null
					}
					endAdornment={
						<>
							{endAdornment}
							<InputAdornment>
								<InputButton
									className={baseClass.element("dropdown-trigger").toClassName()}
									onClick={handleOnTriggerClick}
									tabIndex={-1}
									disabled={readOnly}
								>
									<Icon
										className={baseClass.element("dropdown-trigger-icon").toClassName()}
										name="mdi/menu-down"
										size={ICON_SIZE.XSMALL}
									/>
								</InputButton>
							</InputAdornment>
						</>
					}
				/>
			</div>

			<FadeInOut
				isVisible={dropdownOpen}
				fadeOutDelay={250}
				render={(isVisible) => {
					const dropdownClass = BEM("edt-select-popup", null, {
						type: comboBox ? "combobox" : "select",
						expanded: dropdownOpen,
						visible: isVisible
					});

					if (className) {
						dropdownClass.add(className);
					}

					if (popupClassName) {
						dropdownClass.add(popupClassName);
					}

					return (
						<FloatingContainer
							containerRef={containerRef}
							parent={fieldControlRef}
							anchor={FC_ANCHOR.CENTER_BOTTOM}
							direction={FC_DIRECTION.HR}
							size={FC_SIZE.FIT_MIN}
							className={dropdownClass.toClassName()}
							onClose={closeDropdown}
						>
							<List
								role="listbox"
								autoFocusItem={focusOnListItem}
								className={dropdownClass.element("options").toClassName()}
								disableListWrap
								variant="selectedMenu"
							>
								{items}
							</List>
						</FloatingContainer>
					);
				}}
			/>
		</>
	);
};
